/* src/components/hooks/useAusencias.ts */

import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import type { AusenciaDB, DiaAusencia } from '@/components/types/typeCalendar';

/* ============================================================
   TIPOS
============================================================ */

export interface Ausencia {
  id_ausencia: string;
  id_usuario: string;
  id_empresa: string;
  nombre_completo: string;
  email: string;
  fecha_inicio: string;
  fecha_fin: string;
  tipo_ausencia: string;
  estado_ausencia: string;
}

interface CrearAusenciaInput {
  idUsuario: string;
  idEmpresa: string;
  idTipoAusencia: string;
  fechaInicio: Date;
  fechaFin: Date;
  diasFestivos?: string[];
}

/* Convierte Date → 'YYYY-MM-DD' formato ISO sueco */
const toISO = (d: Date) => d.toLocaleDateString('sv-SE');

/* Expandir rango a lista de días, excluyendo festivos si se indican */
const expandRange = (
  inicio: Date,
  fin: Date,
  estado: string,
  diasFestivos: string[] = []
): DiaAusencia[] => {
  const out: DiaAusencia[] = [];
  const current = new Date(inicio);
  while (current <= fin) {
    const fecha = toISO(current);
    if (!diasFestivos.includes(fecha)) {
      out.push({ fecha, estado });
    }
    current.setDate(current.getDate() + 1);
  }
  return out;
};

/* ============================================================
   1) AUSENCIAS POR USUARIO (SOLO APROBADAS)
============================================================ */
export function useAusenciasPorUsuario(
  idUsuario: string | null,
  idEmpresa: string
) {
  const [ausencias, setAusencias] = useState<DiaAusencia[]>([]);

  useEffect(() => {
    const fetch = async () => {
      const { data, error } = await supabase
        .from('vista_ausencias')
        .select('*')
        .eq('id_usuario', idUsuario)
        .eq('id_empresa', idEmpresa)
        .in('estado_ausencia', ['aprobado', 'pendiente']);

      if (error) {
        toast.error('Error al cargar ausencias por usuario');
        return;
      }

      const raw = (data as AusenciaDB[]) || [];
      const dias: DiaAusencia[] = [];

      raw.forEach((a) => {
        const start = new Date(a.fecha_inicio);
        const end = new Date(a.fecha_fin);
        dias.push(...expandRange(start, end, a.estado_ausencia));
      });

      setAusencias(dias);
    };

    if (!idUsuario || !idEmpresa) return;
    fetch();
  }, [idUsuario, idEmpresa]);

  return ausencias;
}

/* ============================================================
   2) AUSENCIAS POR EMPRESA + CREAR + ACTUALIZAR
============================================================ */
export function useAusenciasPorEmpresa(idEmpresa: string) {
  const [ausencias, setAusencias] = useState<Ausencia[]>([]);

  /* Cargar ausencias */
  const fetchAusencias = async () => {
    const { data, error } = await supabase
      .from('vista_ausencias')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .order('fecha_inicio', { ascending: true });

    if (error) {
      toast.error('Error al cargar ausencias de empresa');
    } else {
      setAusencias(data as Ausencia[]);
    }
  };

  useEffect(() => {
    if (!idEmpresa) return;
    fetchAusencias();
  }, [idEmpresa]);

  /* ============================================================
     CREAR AUSENCIA (nuevo)
  ============================================================ */
  const crearAusencia = async ({
    idUsuario,
    idEmpresa,
    idTipoAusencia,
    fechaInicio,
    fechaFin,
    diasFestivos = [],
  }: CrearAusenciaInput) => {
    try {
      const toastId = toast.loading('Creando ausencia...');

      // Obtener info del tipo
      const { data: tipoData, error: tipoError } = await supabase
        .from('vista_tipo_ausencia')
        .select('*')
        .eq('id_tipo_ausencia', idTipoAusencia)
        .single();

      if (tipoError || !tipoData) {
        toast.error('Tipo de ausencia no encontrado');
        toast.dismiss(toastId);
        return { ok: false };
      }

      const tipoNombre = tipoData.tipo.toLowerCase();

      // Estado inicial
      const estadoInicial =
        tipoNombre === 'enfermedad' ? 'aprobado' : 'pendiente';

      // Buscar ID estado
      const { data: estadoData, error: estadoError } = await supabase
        .from('estado_ausencia')
        .select('id_estado_ausencia')
        .eq('estado', estadoInicial)
        .single();

      if (estadoError || !estadoData) {
        toast.error('No se pudo obtener el estado inicial');
        toast.dismiss(toastId);
        return { ok: false };
      }

      const idEstado = estadoData.id_estado_ausencia;

      /* Insertar ausencia (fecha_inicio/fecha_fin son el rango completo
         elegido; los festivos intermedios simplemente no se cuentan
         como día de ausencia consumido) */
      const { error: insError } = await supabase
        .from('ausencias_solicitadas')
        .insert({
          id_usuario: idUsuario,
          id_empresa: idEmpresa,
          id_tipo_ausencia: idTipoAusencia,
          id_estado_ausencia: idEstado,
          fecha_inicio: toISO(fechaInicio),
          fecha_fin: toISO(fechaFin),
          usuario_creacion: idUsuario,
        });

      if (insError) {
        toast.error('Error al registrar ausencia');
        toast.dismiss(toastId);
        return { ok: false };
      }

      const dias = expandRange(
        fechaInicio,
        fechaFin,
        estadoInicial,
        diasFestivos
      );

      toast.dismiss(toastId);

      await fetchAusencias();

      return {
        ok: true,
        dias,
        estado: estadoInicial,
      };
    } catch (error) {
      Sentry.captureException(error, { extra: { idTipoAusencia, idEmpresa } }); // 👈
      console.error(error);
      toast.error('No se pudo registrar la ausencia');
      return { ok: false };
    }
  };

  /* ============================================================
     ACTUALIZAR ESTADO AUSENCIA
  ============================================================ */
  const updateEstadoAusencia = async (
    id_ausencia: string,
    estado: 'pendiente' | 'aprobado' | 'rechazado',
    motivo?: string,
    usuarioEdicion?: string
  ) => {
    const { data: estadoData, error: estadoError } = await supabase
      .from('estado_ausencia')
      .select('id_estado_ausencia')
      .eq('estado', estado)
      .single();

    if (estadoError || !estadoData) {
      toast.error('No se pudo obtener el estado');
      return { ok: false };
    }

    const updateData: any = {
      id_estado_ausencia: estadoData.id_estado_ausencia,
      fecha_respuesta: new Date().toISOString(),
    };

    if (estado === 'rechazado' && motivo) updateData.motivo = motivo;
    if (usuarioEdicion) updateData.usuario_edicion = usuarioEdicion;

    const { error } = await supabase
      .from('ausencias_solicitadas')
      .update(updateData)
      .eq('id_ausencia', id_ausencia);

    if (error) {
      Sentry.captureException(error, { extra: { id_ausencia, estado } }); // 👈
      toast.error('Error al actualizar estado');
      return { ok: false };
    }

    toast.success(`Ausencia ${estado}`);
    await fetchAusencias();

    return {
      ok: true,
      ausencia: ausencias.find((a) => a.id_ausencia === id_ausencia),
    };
  };

  return {
    ausencias,
    refetch: fetchAusencias,
    crearAusencia,
    updateEstadoAusencia,
  };
}

/* ============================================================
   3) TIPOS AUSENCIA
============================================================ */
export function useTiposAusencia() {
  const [tipos, setTipos] = useState<
    { id_tipo_ausencia: string; tipo: string }[]
  >([]);

  useEffect(() => {
    const fetchTipos = async () => {
      const { data, error } = await supabase
        .from('vista_tipo_ausencia')
        .select('id_tipo_ausencia, tipo');

      if (error) {
        toast.error('Error al cargar tipos de ausencia');
      } else {
        setTipos(data as any[]);
      }
    };
    fetchTipos();
  }, []);

  return tipos;
}

/* ============================================================
   4) ESTADOS AUSENCIA
============================================================ */
export function useEstadosAusencia() {
  const [estados, setEstados] = useState<
    { id_estado_ausencia: string; estado: string }[]
  >([]);

  useEffect(() => {
    const fetchEstados = async () => {
      const { data, error } = await supabase
        .from('estado_ausencia')
        .select('id_estado_ausencia, estado');

      if (error) {
        toast.error('Error al cargar estados de ausencia');
      } else {
        setEstados(data as any[]);
      }
    };

    fetchEstados();
  }, []);

  return estados;
}

/* ============================================================
   5) SOLICITUDES DEL USUARIO
============================================================ */
export function useAusenciasSolicitadas(idUsuario: string, idEmpresa: string) {
  const [solicitudes, setSolicitudes] = useState<
    {
      id_solicitud: number;
      id_tipo_ausencia: string;
      id_estado_ausencia: string;
      fecha_inicio: string;
      fecha_fin: string;
      usuario_creacion: string;
    }[]
  >([]);

  useEffect(() => {
    const fetchSolicitudes = async () => {
      const { data, error } = await supabase
        .from('ausencias_solicitadas')
        .select('*')
        .eq('id_usuario', idUsuario)
        .eq('id_empresa', idEmpresa);

      if (error) {
        toast.error('Error al cargar solicitudes de ausencia');
      } else {
        setSolicitudes(data as any[]);
      }
    };

    if (idUsuario && idEmpresa) fetchSolicitudes();
  }, [idUsuario, idEmpresa]);

  return solicitudes;
}